import React, { useState } from "react";
import AIMonitoringTab from "./AIMonitoringTab";
import AnalyticsTab from "./AnalyticsTab";
import MotionSettingsTab from "./MotionSettingsTab";

export type AnalyticsWorkspaceCamera = {
  cameraId: string;
  name?: string;
  thumbnailUrl?: string;
  planType?: string;
  planName?: string;
  billingModel?: string;
  billingMasterPlanId?: string;
  billingRetailPlanId?: string;
  billingAnalyticsTier?: string;
  analyticsConfig?: unknown;
  aiMonitoringConfig?: unknown;
  motionConfig?: unknown;
  motionEventsEnabled?: boolean;
  motionEventsStatus?: string;
  motionEventsError?: string;
  motionEventsLastSeenAt?: string;
  [key: string]: unknown;
};

type Props = {
  camera: AnalyticsWorkspaceCamera;
  onUpdateCamera: (camera: any) => void;
};

type AnalyticsSection = "objects" | "ai" | "motion";

export function isPlanAnalyticsLocked(camera: AnalyticsWorkspaceCamera) {
  const billingModel = String(camera.billingModel || "").trim().toUpperCase();
  if (billingModel === "PAYG" || billingModel.startsWith("PAYG_")) return false;

  const planType = String(camera.planType || "").trim().toLowerCase();
  const hasFixedPlan =
    planType === "partner" ||
    Boolean(
      camera.billingMasterPlanId ||
        camera.billingRetailPlanId ||
        billingModel,
    );

  return (
    hasFixedPlan &&
    String(camera.billingAnalyticsTier || "").trim().toUpperCase() === "NONE"
  );
}

export default function AnalyticsWorkspace({ camera, onUpdateCamera }: Props) {
  const [activeSection, setActiveSection] = useState<AnalyticsSection>("objects");
  const planAnalyticsLocked = isPlanAnalyticsLocked(camera);
  const fixedPlanName = String(camera.planName || "").trim() || "The selected plan";

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <nav
        aria-label="Analytics settings sections"
        style={{
          display: "flex",
          width: "fit-content",
          maxWidth: "100%",
          gap: 4,
          padding: 4,
          border: "1px solid #dbe2ea",
          borderRadius: 12,
          background: "#f8fafc",
          flexWrap: "wrap",
        }}
      >
        <SectionButton
          active={activeSection === "objects"}
          label="Object Detection"
          description={
            planAnalyticsLocked
              ? "Not included in the selected plan"
              : "Person, Car and detection areas"
          }
          disabled={planAnalyticsLocked}
          onClick={() => setActiveSection("objects")}
        />
        <SectionButton
          active={activeSection === "ai"}
          label="AI Monitoring"
          description="Separate token-metered add-on"
          onClick={() => setActiveSection("ai")}
        />
        <SectionButton
          active={activeSection === "motion"}
          label="Motion Settings"
          description="Camera motion sensitivity and regions"
          onClick={() => setActiveSection("motion")}
        />
      </nav>

      {planAnalyticsLocked && activeSection === "objects" && (
        <div role="status" style={planLockStyle}>
          <div>
            <strong style={{ display: "block", color: "#334155" }}>
              Analytics not included in this plan
            </strong>
            <span>
              {fixedPlanName} is a recording-only plan. Object Detection remains
              visible below but cannot run or be changed. AI Monitoring remains
              available only as the separate token-metered add-on.
            </span>
          </div>
          <span style={lockedBadgeStyle}>Managed by fixed plan</span>
        </div>
      )}

      {activeSection === "objects" && (
        <fieldset
          aria-label="Object Detection settings"
          disabled={planAnalyticsLocked}
          data-plan-locked={String(planAnalyticsLocked)}
          style={{
            ...lockedPanelStyle,
            ...(planAnalyticsLocked ? lockedPanelDisabledStyle : {}),
          }}
        >
          <AnalyticsTab camera={camera as any} onUpdateCamera={onUpdateCamera} />
        </fieldset>
      )}
      {activeSection === "ai" && (
        <AIMonitoringTab camera={camera as any} onUpdateCamera={onUpdateCamera} />
      )}
      {activeSection === "motion" && (
        <MotionSettingsTab camera={camera as any} onUpdateCamera={onUpdateCamera} />
      )}
    </div>
  );
}

function SectionButton({
  active,
  label,
  description,
  disabled = false,
  onClick,
}: {
  active: boolean;
  label: string;
  description: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: 180,
        display: "grid",
        gap: 2,
        padding: "9px 13px",
        border: active ? "1px solid #93c5fd" : "1px solid transparent",
        borderRadius: 9,
        color: disabled ? "#94a3b8" : active ? "#1d4ed8" : "#475569",
        background: disabled ? "#eef2f7" : active ? "#ffffff" : "transparent",
        boxShadow: active ? "0 2px 8px rgba(15, 23, 42, 0.08)" : "none",
        textAlign: "left",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.72 : 1,
      }}
    >
      <strong style={{ fontSize: 12 }}>{label}</strong>
      <span style={{ fontSize: 10, color: active ? "#64748b" : "#94a3b8" }}>
        {description}
      </span>
    </button>
  );
}

const planLockStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "14px 16px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  background: "#f1f5f9",
  color: "#64748b",
  fontSize: 12,
  lineHeight: 1.5,
};

const lockedBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: "5px 9px",
  border: "1px solid #cbd5e1",
  borderRadius: 999,
  background: "#e2e8f0",
  color: "#475569",
  fontSize: 11,
  fontWeight: 800,
};

const lockedPanelStyle: React.CSSProperties = {
  minInlineSize: 0,
  margin: 0,
  padding: 0,
  border: 0,
};

const lockedPanelDisabledStyle: React.CSSProperties = {
  padding: 12,
  border: "1px solid #cbd5e1",
  borderRadius: 14,
  background: "#f1f5f9",
  opacity: 0.72,
  cursor: "not-allowed",
};
